import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { Calendar } from 'calendar-base';
import dateUtils from '../calendar-utils/dateUtils/index.esm.js';
import { getWeekStartIndex, daysInWeek } from '../calendar-utils/intl/index.esm.js';
import todayUtil from '../calendar-utils/today/index.esm.js';
const sixWeeks = 6;
const sixWeeksInDays = sixWeeks * daysInWeek;
const useCalendarDays = ({
  numMonthsToDisplay,
  year,
  month,
  showSiblingMonths: siblingMonths,
  locale,
  maxDate,
  minDate,
  padWeeks
}) => {
  const l10n = useLocalization();
  const today = React.useMemo(() => todayUtil.getToday(), []);
  const maxDateDay = React.useMemo(() => todayUtil.getDateObject(maxDate), [maxDate]);
  const minDateDay = React.useMemo(() => todayUtil.getDateObject(minDate), [minDate]);
  const calendarData = React.useMemo(() => {
    const weekStart = getWeekStartIndex(l10n);
    return new Calendar({
      siblingMonths,
      weekStart
    });
  }, [l10n, siblingMonths]);
  const daysByWeekAndMonth = React.useMemo(() => {
    const monthIndexes = Array.from(Array(numMonthsToDisplay).keys());
    return monthIndexes.map(monthIndex => {
      const {
        updatedMonth,
        updatedYear
      } = dateUtils.addMonthsToMonth(year, month, monthIndex);
      let monthDays = calendarData.getCalendar(updatedYear, updatedMonth).map(day => day ? {
        ...day,
        dateString: dateUtils.getDayToString(day),
        localeDateString: new Date(Date.UTC(day.year, day.month, day.day)).toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
          weekday: 'long'
        }),
        isToday: todayUtil.isToday(day, today),
        isPast: todayUtil.isPast(day, today),
        isPastMaxDate: maxDateDay ? todayUtil.isAfter(day, maxDateDay) : false,
        isMinDate: minDateDay ? todayUtil.isToday(day, minDateDay) : false,
        isBeforeMinDate: minDateDay ? todayUtil.isPast(day, minDateDay) : false,
        position: {
          monthIndex
        }
      } : false);
      // This will make all months to have 6 weeks
      if (padWeeks) {
        const extraDaysToCompleteSixWeeks = sixWeeksInDays - monthDays.length;
        const extraDays = Array.from(Array(extraDaysToCompleteSixWeeks)).map(() => false);
        monthDays = monthDays.concat(extraDays);
      }
      const weeksWithPosition = dateUtils.groupMonthDaysInWeeks(monthDays).map((week, weekIndex) => week.map((day, dayIndex) => day ? {
        ...day,
        position: {
          ...day.position,
          weekIndex,
          dayIndex
        }
      } : day));
      return {
        weeks: weeksWithPosition,
        month: updatedMonth,
        year: updatedYear
      };
    });
  }, [calendarData, locale, maxDateDay, minDateDay, month, numMonthsToDisplay, today, year, padWeeks]);
  return daysByWeekAndMonth;
};
export { sixWeeks, useCalendarDays };